/**
 * Client entrypoint. Gates the Phaser boot on a valid JWT.
 *
 * Flow:
 *   - On load, check sessionStorage for an existing token. If present, hide
 *     the auth gate and boot the game immediately.
 *   - Otherwise wire up the login/register form. On success, store the token
 *     and boot the game.
 *   - The Socket.IO connection (built here, used by future scenes via the
 *     Phaser registry) is created with the JWT in the handshake — the
 *     server's authMiddleware validates it before any 'connection' fires.
 */
import Phaser from 'phaser';
import { io, type Socket } from 'socket.io-client';
import { SocketEvents, type SessionTerminatedPayload } from '@pokemiu/shared';
import {
  clearStoredToken,
  getStoredToken,
  login,
  readPayload,
  register,
  setStoredToken,
  type Credentials,
} from './auth.js';
import { BootScene } from './scenes/BootScene.js';
import { OverworldScene } from './scenes/OverworldScene.js';
import { BattleScene } from './scenes/BattleScene.js';
import { mountTouchControls } from './touchControls.js';

const API_BASE: string =
  (import.meta.env.VITE_SOCKET_URL as string | undefined) ?? 'http://localhost:3000';

const gateEl = document.getElementById('auth-gate') as HTMLFormElement;
const usernameEl = document.getElementById('auth-username') as HTMLInputElement;
const passwordEl = document.getElementById('auth-password') as HTMLInputElement;
const loginBtn = document.getElementById('auth-login') as HTMLButtonElement;
const registerBtn = document.getElementById('auth-register') as HTMLButtonElement;
const errorEl = document.getElementById('auth-error') as HTMLParagraphElement;
const welcomeEl = document.getElementById('welcome') as HTMLParagraphElement;
const gameEl = document.getElementById('game') as HTMLDivElement;
const logoutBtn = document.getElementById('logout') as HTMLButtonElement;
const controlsEl = document.getElementById('controls') as HTMLDivElement;

function setError(message: string): void {
  errorEl.textContent = message;
}

function setBusy(busy: boolean): void {
  loginBtn.disabled = busy;
  registerBtn.disabled = busy;
}

function bootGame(token: string): void {
  gateEl.style.display = 'none';
  gameEl.classList.add('active');
  logoutBtn.style.display = 'block';
  controlsEl.classList.add('active');
  mountTouchControls();
  const socket: Socket = io(API_BASE, {
    auth: { token },
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    randomizationFactor: 0.5,
  });
  socket.on(SocketEvents.SESSION_TERMINATED, (p: SessionTerminatedPayload) => {
    // Server kicked this socket because the same account logged in elsewhere.
    // Clear the local token so the user can re-auth (or log in on the other tab).
    clearStoredToken();
    alert(`Session ended: ${p.message}. Reloading.`);
    window.location.reload();
  });
  logoutBtn.addEventListener(
    'click',
    () => {
      clearStoredToken();
      socket.disconnect();
      window.location.reload();
    },
    { once: true },
  );
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'game',
    width: 320,
    height: 240,
    pixelArt: true,
    backgroundColor: '#1a1a2e',
    scene: [BootScene, OverworldScene, BattleScene],
    scale: {
      // FIT preserves the 4:3 logical canvas aspect ratio inside whatever
      // size the parent #game div has — full viewport on mobile, capped on
      // desktop (see CSS media query). CENTER_BOTH letterboxes evenly.
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
  });
  game.registry.set('socket', socket);
  game.registry.set('token', token);
  game.registry.set('jwtPayload', readPayload(token));
}

async function attempt(action: 'login' | 'register'): Promise<void> {
  setError('');
  const creds: Credentials = {
    username: usernameEl.value.trim(),
    password: passwordEl.value,
  };
  if (!creds.username || !creds.password) {
    setError('Username and password are required.');
    return;
  }
  setBusy(true);
  try {
    const token = action === 'login' ? await login(creds) : await register(creds);
    setStoredToken(token);
    bootGame(token);
  } catch (err) {
    setError(err instanceof Error ? err.message : 'Unknown error');
  } finally {
    setBusy(false);
  }
}

function showResumeBanner(token: string): void {
  const payload = readPayload(token);
  if (!payload) return;
  welcomeEl.innerHTML = '';
  const text = document.createTextNode(`Resuming session as ${payload.username} — `);
  const link = document.createElement('button');
  link.type = 'button';
  link.textContent = 'log out';
  link.addEventListener('click', () => {
    clearStoredToken();
    welcomeEl.textContent = '';
  });
  welcomeEl.appendChild(text);
  welcomeEl.appendChild(link);
}

function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  // When a freshly-activated SW takes over the page, reload once so the user
  // immediately sees the new shell instead of being stuck on whatever HTML
  // was rendered from the previous (now-replaced) SW's cache. The `refreshing`
  // guard prevents loops if controllerchange fires more than once.
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
  // Defer until after first paint so the SW install doesn't compete with
  // critical-path JS for bandwidth on a cold load.
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('[sw] registration failed:', err);
    });
  });
}

function init(): void {
  registerServiceWorker();
  const existing = getStoredToken();
  if (existing) {
    bootGame(existing);
    return;
  }
  showResumeBanner('');
  loginBtn.addEventListener('click', () => {
    void attempt('login');
  });
  registerBtn.addEventListener('click', () => {
    void attempt('register');
  });
  gateEl.addEventListener('submit', (e) => {
    e.preventDefault();
    void attempt('login');
  });
}

init();
